import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useConfirm } from "../../contexts/confirm.text";
import { FormatReportDateTime, FormatReportDateTimeJSON } from "../../Logic/DateTimeFormatters";
import { RootState } from "../../state/store";
import { getSessionValue, saveSessionValue } from "../../utilities/localStore/calls";
import DropdownBasic from "../Generic/DropdownBasic";
import FindDropdown from "../Generic/FindDropdown";
import { formatDatePeriods } from "./logic";
import MISReport from "./misReport";
import ResponseCycleSLA from "./responseCycleSLA";
import { GetDepartments, GetMISbyMailboxKey, GetUsers } from "./tenantApiCalls";
import UserProductivityReport from "./userProductivityReport-table";
export interface reportFilter {
    report: string;
    timePeriod: string;
    dateTo: Date | string;
    dateFrom: Date | string;
    users: number[];
    serviceCentres: number[];
    mailboxes: number[];
    type: number;
    primaryfield?: iField;
    generateReport: boolean; // this value is used to indicate weather or not its ready to send to API to get data
}

export interface iDropdown {
    title: string;
    value: number;
}
export interface iField {
    fieldLabel: string;
    fieldKey: number;
}
function Reports() {
    const [users, setUsers] = React.useState<iDropdown[]>([]);
    const [departments, setDepartments] = React.useState<iDropdown[]>([]);
    const [mailboxes, setMailboxes] = React.useState<iDropdown[]>([]);
    const [primaryFields, setprimaryFields] = React.useState<iField[]>([]);
    const [allPrimaryKey, setAllPrimaryKey] = React.useState<JSX.Element[]>([]);
    const currentUser = useSelector((state: RootState) => state.UserInformation);
    const permissions = useSelector((state: RootState) => state.permissions);
    const [disableGenerateButton, setDisableGenerateButton] = useState(false);

    const { confirm } = useConfirm();

    useEffect(() => {
        (async () => {
            var uData: iDropdown[] | null = getSessionValue("users");
            var dData: iDropdown[] | null = getSessionValue("departments");
            if (!uData) {
                let userData = await GetUsers();
                if (userData.isOk && userData.data) {
                    let allUsers = userData.data.users
                        .filter(c => c.blocked == false)
                        .map((x: any) => {
                            return { title: x.firstname + " " + x.lastname, value: x.id };
                        });
                    saveSessionValue(
                        "users",
                        allUsers.sort((a: any, b: any) => a.title.localeCompare(b.title)),
                    );
                    setUsers(allUsers.sort((a: any, b: any) => a.title.localeCompare(b.title)));
                }
            } else {
                setUsers(uData);
            }
            if (!dData) {
                var departmentData = await GetDepartments();
                if (departmentData.isOk && departmentData.data) {
                    let departmentsWithMailboxes = currentUser.mailboxes.flatMap(c => c.serviceCentreID);

                    let allDepartments = departmentData.data.items
                        .filter(c => departmentsWithMailboxes.includes(c.id))
                        .map((x: any) => {
                            return { title: x.name, value: x.id };
                        });
                    saveSessionValue(
                        "departments",
                        allDepartments.sort((a: any, b: any) => a.title.localeCompare(b.title)),
                    );
                    setDepartments(allDepartments.sort((a: any, b: any) => a.title.localeCompare(b.title)));
                }
            } else {
                setDepartments(dData);
            }
            let allMailBoxesUserHasAccessTo = currentUser.mailboxes
                .map(x => {
                    return {
                        title: x.name,
                        value: x.id,
                    };
                })
                .sort((a, b) => a.title.localeCompare(b.title));
            let mailBoxesUserCanviewReport = permissions.Mailboxes.filter(x => x.Permissions.ViewReports);
            saveSessionValue(
                "mailboxes",
                allMailBoxesUserHasAccessTo.filter(x => mailBoxesUserCanviewReport.some(c => c.ID == x.value)),
            );
            setMailboxes(
                allMailBoxesUserHasAccessTo.filter(x => mailBoxesUserCanviewReport.some(c => c.ID == x.value)),
            );

            if (allMailBoxesUserHasAccessTo.length > 0) {
                setReportFilters({
                    ...reportFilters,
                    generateReport: true,
                    mailboxes: [allMailBoxesUserHasAccessTo[0].value],
                });
            }
        })();
    }, []);
    const [reportFilters, setReportFilters] = useState<reportFilter>({
        report: "Productivity",
        timePeriod: "Today",
        dateTo: FormatReportDateTime(new Date(), "23:59"),
        dateFrom: FormatReportDateTime(new Date(), "00:00"),
        users: permissions.Tenant.ElevatedReports == true ? users.flatMap(x => x.value) : [],
        serviceCentres: departments.flatMap(x => x.value),
        mailboxes: mailboxes.flatMap(x => x.value),
        type: 1,
        primaryfield: undefined,
        generateReport: true,
    });

    const inputDateStartRef = React.useRef<HTMLInputElement>(null);
    const inputDateEndRef = React.useRef<HTMLInputElement>(null);

    let defaultParams = {
        report: "Productivity",
        timePeriod: "Today",
        dateTo: FormatReportDateTime(new Date(), "23:59"),
        dateFrom: FormatReportDateTime(new Date(), "00:00"),
        users: permissions.Tenant.ElevatedReports == true ? users.flatMap(x => x.value) : [],
        serviceCentres: departments.flatMap(x => x.value),
        mailboxes: mailboxes.flatMap(x => x.value),
        type: 1,
        generateReport: false,
    };
    const changeReport = (report: string, generateReport: boolean = true) => {
        let params = defaultParams;
        params.generateReport = generateReport;
        params.report = report;
        let allMailBoxesUserHasAccessTo = currentUser.mailboxes
            .map(x => {
                return {
                    title: x.name,
                    value: x.id,
                };
            })
            .sort((a, b) => a.title.localeCompare(b.title));
        let mailBoxesUserCanviewReport = permissions.Mailboxes.filter(x => x.Permissions.ViewReports);
        saveSessionValue(
            "mailboxes",
            allMailBoxesUserHasAccessTo.filter(x => mailBoxesUserCanviewReport.some(c => c.ID == x.value)),
        );
        setMailboxes(allMailBoxesUserHasAccessTo.filter(x => mailBoxesUserCanviewReport.some(c => c.ID == x.value)));

        if (allMailBoxesUserHasAccessTo.length > 0) {
            setReportFilters({
                ...reportFilters,
                generateReport: true,
                mailboxes: [allMailBoxesUserHasAccessTo[0].value],
            });
        }
        switch (report) {
            case "SLA":
                var date = new Date();
                if (date.getDate() > 1) {
                    //if its the first day of month then the dates should be the same as productivity
                    var firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
                    params.dateFrom = FormatReportDateTime(firstDay, "00:00");
                    params.dateTo = FormatReportDateTime(new Date(), "00:00");
                    params.timePeriod = "Custom Period";
                }
                params.mailboxes = [allMailBoxesUserHasAccessTo[0].value];
                break;
            case "MIS Summary":
                var date = new Date();
                if (date.getDate() > 1) {
                    //if its the first day of month then the dates should be the same as productivity
                    var firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
                    params.dateFrom = FormatReportDateTime(firstDay, "00:00");
                    params.dateTo = FormatReportDateTime(new Date(), "00:00");
                    params.timePeriod = "Custom Period";
                }
                params.generateReport = false;
                setDisableGenerateButton(true);
                break;
        }
        setReportFilters(params);
    };

    const changeTimePeriod = (period: string) => {
        let { startDate, endDate } = formatDatePeriods(period);
        setReportFilters({
            ...reportFilters,
            generateReport: false,
            timePeriod: period,
            dateFrom: startDate,
            dateTo: endDate,
        });
        //setTimeLabel(period);
    };
    const changeTypeVal = (value: number) => {
        setReportFilters({ ...reportFilters, generateReport: false, type: value });
    };

    const changeDepartmentsVal = (value: number[]) => {
        let mailboxesByDepartments = currentUser.mailboxes
            .filter(c => value.includes(c.serviceCentreID))
            .flatMap(z => z.id);

        setReportFilters({
            ...reportFilters,
            generateReport: false,
            serviceCentres: value,
            mailboxes: mailboxesByDepartments,
        });
    };

    const changeMailboxesVal = async (value: number[]) => {
        setReportFilters({ ...reportFilters, generateReport: false, mailboxes: value });

        var response = await GetMISbyMailboxKey(value[0]);
        var fields = response.data?.misModel.fields;

        const stringFields = fields?.filter((field: any) => field.fieldType === "Dropdown");

        if (stringFields !== undefined) {
            const dropdownItems: iField[] = stringFields.map((field: any) => ({
                fieldKey: field.id,
                fieldLabel: field.label,
            }));
            setprimaryFields(dropdownItems);
        }
    };
    React.useEffect(() => {
        const items = primaryFields.map(x => (
            <li
                key={x.fieldKey}
                title={x.fieldLabel}
            >
                <button onClick={() => changePrimaryFieldValue(x.fieldKey, x.fieldLabel)}>{x.fieldLabel}</button>
            </li>
        ));

        setAllPrimaryKey(items);
    }, [primaryFields]);

    const changePrimaryFieldValue = (value: number, title: string) => {
        let field: iField = { fieldLabel: title, fieldKey: value };
        setReportFilters({ ...reportFilters, generateReport: false, primaryfield: field });
        setDisableGenerateButton(false);
    };

    const changeUsersVal = (value: number[]) => {
        setReportFilters({ ...reportFilters, generateReport: false, users: value });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.currentTarget) {
            return;
        }
        if (e.currentTarget.id == "startDate") {
            setReportFilters({
                ...reportFilters,
                generateReport: false,
                dateFrom: e.currentTarget.value,
                timePeriod: "Custom Period",
            });
        } else if (e.currentTarget.id == "endDate") {
            setReportFilters({
                ...reportFilters,
                generateReport: false,
                dateTo: e.currentTarget.value,
                timePeriod: "Custom Period",
            });
        }
    };
    const getReportData = async () => {
        let canGenerateReport = true;
        if (disableGenerateButton) {
            await confirm({
                message: "Please ensure that all fields are selected to generate the report",
                onConfirm: () => {
                    canGenerateReport = false;
                    return;
                },
                onCancel: () => {
                    canGenerateReport = false;
                    return;
                },
            });
        }

        if (
            isNaN(Date.parse(reportFilters.dateTo.toString())) ||
            isNaN(Date.parse(reportFilters.dateFrom.toString()))
        ) {
            await confirm({
                message: "Please capture a valid 'Start' and 'End' date",
                onConfirm: () => {
                    canGenerateReport = false;
                    return;
                },
                onCancel: () => {
                    canGenerateReport = false;
                    return;
                },
            });
        }

        if (new Date(reportFilters.dateTo) < new Date(reportFilters.dateFrom)) {
            await confirm({
                message: "End Date cannot be earlier than Start date",
                onConfirm: () => {
                    canGenerateReport = false;
                    return;
                },
                onCancel: () => {
                    canGenerateReport = false;
                    return;
                },
            });
        }
        if (canGenerateReport) {
            setReportFilters({ ...reportFilters, generateReport: true });
        }
    };

    const departments_ = departments.map(x => {
        return (
            <li
                key={x.title}
                title={x.title}
            >
                <button
                    onClick={() => {
                        changeDepartmentsVal([x.value]);
                    }}
                >
                    {x.title}
                </button>
            </li>
        );
    });
    departments_.unshift(
        <li
            key={"All Departments"}
            title={"All Departments"}
        >
            <button
                onClick={() => {
                    changeDepartmentsVal(departments.flatMap(x => x.value));
                }}
            >
                {"All Departments"}
            </button>
        </li>,
    );

    let allUsers_ = users.map(x => {
        return (
            <li
                key={x.value}
                title={x.title}
            >
                <button
                    onClick={() => {
                        changeUsersVal([x.value]);
                    }}
                >
                    {x.title}
                </button>
            </li>
        );
    });
    allUsers_.unshift(
        <li
            key={"All Users"}
            title={"All Users"}
        >
            <button
                onClick={() => {
                    changeUsersVal(users.flatMap(x => x.value));
                }}
            >
                {"All Users"}
            </button>
        </li>,
    );

    let allMailboxes_: JSX.Element[] = [];
    if (reportFilters.serviceCentres.length != 1) {
        allMailboxes_ = mailboxes.map(x => {
            return (
                <li
                    key={x.value}
                    title={x.title}
                >
                    <button
                        onClick={() => {
                            changeMailboxesVal([x.value]);
                        }}
                    >
                        {x.title}
                    </button>
                </li>
            );
        });
        allMailboxes_.unshift(
            <li
                key={"All Mailboxes"}
                title={"All Mailboxes"}
            >
                <button
                    onClick={() => {
                        changeMailboxesVal(mailboxes.flatMap(x => x.value));
                    }}
                >
                    {"All Mailboxes"}
                </button>
            </li>,
        );
    } else {
        allMailboxes_ = currentUser.mailboxes
            .filter(c => c.serviceCentreID == reportFilters.serviceCentres[0])
            .map(x => {
                return (
                    <li
                        key={x.id}
                        title={x.name}
                    >
                        <button
                            onClick={() => {
                                changeMailboxesVal([x.id]);
                            }}
                        >
                            {x.name}
                        </button>
                    </li>
                );
            });
        allMailboxes_.unshift(
            <li
                key={"All Mailboxes"}
                title={"All Mailboxes"}
            >
                <button
                    onClick={() => {
                        changeMailboxesVal(
                            currentUser.mailboxes
                                .filter(c => c.serviceCentreID == reportFilters.serviceCentres[0])
                                .flatMap(x => x.id),
                        );
                    }}
                >
                    {"All Mailboxes"}
                </button>
            </li>,
        );
    }
    return (
        <div className="alternateView">
            <div
                className="col1"
                style={{ width: "210px" }}
            >
                <div className="header">Reports</div>
                <div style={{ padding: "5px", clear: "both" }}>
                    <b>Name:</b>
                    <FindDropdown
                        id={1324}
                        label={reportFilters.report}
                        styles={{ width: "100%", border: "solid 1px #CFCFCF" }}
                    >
                        <li
                            key={"Produc_1"}
                            title={"Productivity"}
                        >
                            <button
                                onClick={() => {
                                    changeReport("Productivity");
                                }}
                            >
                                {" "}
                                Productivity
                            </button>
                        </li>
                        <li
                            key={"SLA_1"}
                            title={"SLA"}
                        >
                            <button
                                onClick={() => {
                                    changeReport("SLA");
                                }}
                            >
                                SLA
                            </button>
                        </li>
                        <li
                            key={"MIS_1"}
                            title={"MIS Summary"}
                        >
                            <button
                                onClick={() => {
                                    changeReport("MIS Summary");
                                }}
                            >
                                MIS Summary
                            </button>
                        </li>
                    </FindDropdown>
                </div>
                <div style={{ padding: "5px", clear: "both" }}>
                    <b>Time Period:</b>
                    <FindDropdown
                        id={222}
                        label={reportFilters.timePeriod}
                        styles={{ float: "left", width: "100%", border: "solid 1px #CFCFCF" }}
                    >
                        <li
                            key={"Today_R"}
                            title={"Today"}
                        >
                            <button
                                onClick={() => {
                                    changeTimePeriod("Today");
                                }}
                            >
                                Today
                            </button>
                        </li>

                        <li
                            key={"Week_R"}
                            title={"This Week"}
                        >
                            <button
                                onClick={() => {
                                    changeTimePeriod("This Week");
                                }}
                            >
                                This Week
                            </button>
                        </li>
                        <li
                            key={"Month_R"}
                            title={"This Month"}
                        >
                            <button
                                onClick={() => {
                                    changeTimePeriod("This Month");
                                }}
                            >
                                This Month
                            </button>
                        </li>
                        <li
                            key={"Previous_Day_R"}
                            title={"Previous Day"}
                        >
                            <button
                                onClick={() => {
                                    changeTimePeriod("Previous Day");
                                }}
                            >
                                Previous Day
                            </button>
                        </li>
                        <li
                            key={"Previous_Week_R"}
                            title={"Previous Week"}
                        >
                            <button
                                onClick={() => {
                                    changeTimePeriod("Previous Week");
                                }}
                            >
                                Previous Week
                            </button>
                        </li>
                        <li
                            key={"Previous_Month_R"}
                            title={"Previous Month"}
                        >
                            <button
                                onClick={() => {
                                    changeTimePeriod("Previous Month");
                                }}
                            >
                                Previous Month
                            </button>
                        </li>
                        <li
                            key={"Custom_R"}
                            title={"Custom Period"}
                        >
                            <button
                                onClick={() => {
                                    changeTimePeriod("Custom Period");
                                }}
                            >
                                Custom Period
                            </button>
                        </li>
                    </FindDropdown>
                </div>
                <div style={{ padding: "5px", clear: "both", paddingBottom: "0px" }}>
                    <b>Start Date:</b>
                    <input
                        type="datetime-local"
                        max="9999-12-31T23:59"
                        min="1999-12-31T23:59"
                        className="g-TextBox-default"
                        value={reportFilters.dateFrom.toString().replace("T", " ")}
                        placeholder="Start Date"
                        ref={inputDateStartRef}
                        onChange={handleChange}
                        id="startDate"
                    ></input>
                </div>
                <div style={{ padding: "5px", clear: "both", paddingBottom: "0px" }}>
                    <b>End Date:</b>
                    <input
                        type="datetime-local"
                        max="9999-12-31T23:59"
                        min="1999-12-31T23:59"
                        className="g-TextBox-default"
                        value={reportFilters.dateTo.toString().replace("T", " ")}
                        ref={inputDateEndRef}
                        onChange={handleChange}
                        id="endDate"
                    ></input>
                </div>

                <div style={{ padding: "5px", clear: "both" }}>
                    <b>Departments:</b>

                    <FindDropdown
                        id={222}
                        label={
                            reportFilters.serviceCentres.length == 1
                                ? departments.find(x => x.value == reportFilters.serviceCentres[0])!.title
                                : "All Departments"
                        }
                        styles={{ float: "left", width: "100%", background: "#fff", border: "solid 1px #CFCFCF" }}
                    >
                        {departments_}
                    </FindDropdown>
                </div>
                <div style={{ padding: "5px", clear: "both" }}>
                    <b>Mailboxes:</b>
                    <FindDropdown
                        id={222}
                        label={
                            reportFilters.mailboxes.length == 1
                                ? mailboxes.find(x => x.value == reportFilters.mailboxes[0])!.title
                                : "All Mailboxes"
                        }
                        styles={{ float: "left", width: "100%", border: "solid 1px #CFCFCF" }}
                    >
                        {allMailboxes_}
                    </FindDropdown>
                </div>

                {reportFilters.report == "Productivity" && (
                    <div style={{ padding: "5px", clear: "both" }}>
                        <b>Users:</b>
                        <FindDropdown
                            id={222}
                            label={
                                reportFilters.users.length == 1
                                    ? users.find(x => x.value == reportFilters.users[0])!.title
                                    : permissions.Tenant.ElevatedReports == true
                                      ? "All Users"
                                      : currentUser.firstname + " " + currentUser.lastname
                            }
                            styles={{ float: "left", width: "100%", maxWidth: "210px", border: "solid 1px #CFCFCF" }}
                        >
                            {permissions.Tenant.ElevatedReports == true
                                ? allUsers_
                                : [
                                      <li
                                          key={currentUser.userID}
                                          title={currentUser.firstname + " " + currentUser.lastname}
                                      >
                                          <button
                                              onClick={() => {
                                                  changeUsersVal([currentUser.userID]);
                                              }}
                                          >
                                              {currentUser.firstname + " " + currentUser.lastname}
                                          </button>
                                      </li>,
                                  ]}
                        </FindDropdown>
                    </div>
                )}
                {reportFilters.report == "Productivity" && (
                    <div style={{ padding: "5px", clear: "both" }}>
                        <b>Type:</b>
                        <DropdownBasic
                            id={222}
                            label={reportFilters.type == 1 ? "Summary" : reportFilters.type == 2 ? "By Hour" : "By Day"}
                            styles={{
                                float: "left",
                                width: "100%",
                                maxWidth: "210px",
                                background: "white",
                                border: "solid 1px #CFCFCF",
                            }}
                        >
                            <li
                                key={"Summary"}
                                title={"Summary"}
                            >
                                <button
                                    onClick={() => {
                                        changeTypeVal(1);
                                    }}
                                >
                                    {"Summary"}
                                </button>
                            </li>
                            <li
                                key={"By Day"}
                                title={"By Day"}
                            >
                                <button
                                    onClick={() => {
                                        changeTypeVal(3);
                                    }}
                                >
                                    {"By Day"}
                                </button>
                            </li>

                            <li
                                key={"By Hour"}
                                title={"By Hour"}
                            >
                                <button
                                    onClick={() => {
                                        changeTypeVal(2);
                                    }}
                                >
                                    {"By Hour"}
                                </button>
                            </li>
                        </DropdownBasic>
                    </div>
                )}
                {reportFilters.report == "MIS Summary" && (
                    <div style={{ padding: "5px", clear: "both" }}>
                        <b>Primary Field:</b>
                        <FindDropdown
                            id={222}
                            label={
                                reportFilters.primaryfield !== undefined
                                    ? reportFilters.primaryfield.fieldLabel
                                    : "Select Primary Field"
                            }
                            styles={{ float: "left", width: "100%", maxWidth: "210px", border: "solid 1px #CFCFCF" }}
                        >
                            {allPrimaryKey}
                        </FindDropdown>
                    </div>
                )}
                <div style={{ padding: "5px", clear: "both" }}>
                    <div className="reportButtonRow">
                        <div>
                            <button
                                className="g-btn g-btn-action"
                                disabled={false}
                                onClick={getReportData}
                            >
                                Generate Report
                            </button>
                        </div>

                        <div>
                            <button
                                className="g-btn g-btn-default"
                                onClick={() => {
                                    changeReport(reportFilters.report, false);
                                }}
                            >
                                Reset&nbsp;Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div
                className="col2"
                style={{ padding: "13px" }}
            >
                {reportFilters.report == "Productivity" && (
                    <UserProductivityReport
                        dateFrom={FormatReportDateTimeJSON(reportFilters.dateFrom.toString())}
                        dateTo={FormatReportDateTimeJSON(reportFilters.dateTo.toString())}
                        serviceCentres={reportFilters.serviceCentres}
                        users={permissions.Tenant.ElevatedReports == true ? reportFilters.users : [currentUser.userID]}
                        mailboxes={reportFilters.mailboxes}
                        type={reportFilters.type}
                        generateReport={reportFilters.generateReport}
                    ></UserProductivityReport>
                )}
                {reportFilters.report == "SLA" && (
                    <ResponseCycleSLA
                        dateFrom={FormatReportDateTimeJSON(reportFilters.dateFrom.toString())}
                        dateTo={FormatReportDateTimeJSON(reportFilters.dateTo.toString())}
                        serviceCentres={reportFilters.serviceCentres}
                        users={reportFilters.users}
                        mailboxes={reportFilters.mailboxes}
                        type={reportFilters.type}
                        generateReport={reportFilters.generateReport}
                    ></ResponseCycleSLA>
                )}{" "}
                {reportFilters.report == "MIS Summary" && (
                    <MISReport
                        dateFrom={FormatReportDateTimeJSON(reportFilters.dateFrom.toString())}
                        dateTo={FormatReportDateTimeJSON(reportFilters.dateTo.toString())}
                        serviceCentres={reportFilters.serviceCentres}
                        users={reportFilters.users}
                        mailboxes={reportFilters.mailboxes}
                        type={reportFilters.type}
                        generateReport={reportFilters.generateReport}
                        primaryFieldKey={reportFilters.primaryfield}
                        reportType={3}
                    ></MISReport>
                )}
            </div>
        </div>
    );
}
export default Reports;
